import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", attending: "yes", guests: 1, meal: "", song: "", message: "" };

export default function Rsvp() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/rsvp`, { ...form, guests: Number(form.guests) || 1 });
      setDone(true);
      toast.success("RSVP received — thank you!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="pt-32 md:pt-40 pb-12 max-w-3xl mx-auto px-6 md:px-8 text-center">
        <Reveal>
          <p className="overline text-orange">RSVP</p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl text-ink mt-4 leading-[0.95]">
            Will you dance?
          </h1>
          <p className="font-body text-lg text-ink/80 mt-6">
            Kindly reply by April 22, 2027. Can't wait to celebrate with you.
          </p>
        </Reveal>
      </section>

      <section className="max-w-2xl mx-auto px-6 md:px-8 pb-24 md:pb-32">
        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card-retro bg-butter p-10 md:p-14 text-center"
            data-testid="rsvp-success"
          >
            <p className="font-display text-5xl md:text-6xl text-ink">✦ Thank you! ✦</p>
            <p className="font-body text-lg text-ink/80 mt-5">
              Your RSVP is in, {form.name.split(" ")[0]}. {form.attending === "yes" ? "See you on the dance floor!" : "We'll miss you — sending love."}
            </p>
            <button
              className="btn-retro mt-8"
              onClick={() => { setForm(EMPTY); setDone(false); }}
              data-testid="rsvp-another-button"
            >
              Submit another
            </button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="card-retro bg-cream p-8 md:p-12 space-y-6" data-testid="rsvp-form">
            <div>
              <label className="overline text-ink/70">Full name</label>
              <input
                className="input-retro mt-2"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Jane & John Doe"
                data-testid="rsvp-name-input"
              />
            </div>
            <div>
              <label className="overline text-ink/70">Email</label>
              <input
                type="email"
                className="input-retro mt-2"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@email.com"
                data-testid="rsvp-email-input"
              />
            </div>

            <div>
              <label className="overline text-ink/70">Will you attend?</label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                {[
                  { v: "yes", label: "Joyfully accept" },
                  { v: "no", label: "Regretfully decline" },
                ].map((o) => (
                  <button
                    type="button"
                    key={o.v}
                    onClick={() => set("attending", o.v)}
                    data-testid={`rsvp-attending-${o.v}`}
                    className={`font-grotesk font-bold py-3 rounded-xl border-2 border-ink transition-transform duration-200 ${
                      form.attending === o.v ? "bg-orange text-cream" : "bg-cream text-ink hover:-translate-y-0.5"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {form.attending === "yes" && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-ink/70">Number of guests</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      className="input-retro mt-2"
                      value={form.guests}
                      onChange={(e) => set("guests", e.target.value)}
                      data-testid="rsvp-guests-input"
                    />
                  </div>
                  <div>
                    <label className="overline text-ink/70">Meal preference</label>
                    <input
                      className="input-retro mt-2"
                      value={form.meal}
                      onChange={(e) => set("meal", e.target.value)}
                      placeholder="Chicken / Veg / etc."
                      data-testid="rsvp-meal-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="overline text-ink/70">Song request</label>
                  <input
                    className="input-retro mt-2"
                    value={form.song}
                    onChange={(e) => set("song", e.target.value)}
                    placeholder="What gets you on the floor?"
                    data-testid="rsvp-song-input"
                  />
                </div>
              </>
            )}

            <div>
              <label className="overline text-ink/70">A note for the couple</label>
              <textarea
                rows="3"
                className="input-retro mt-2 resize-none"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Optional — say hi!"
                data-testid="rsvp-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-retro orange w-full justify-center disabled:opacity-60"
              data-testid="rsvp-submit-button"
            >
              {loading ? "Sending…" : "Send RSVP →"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
